import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './BulkTransactionEntry.css';

function BulkTransactionEntry({ onClose, mode }) {
  const [rowPairs, setRowPairs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [saving, setSaving] = useState(false);
  const [nextSequence, setNextSequence] = useState(1);
  const user = auth.currentUser;

  // Initialize with first pair
  useEffect(() => {
    if (rowPairs.length === 0) {
      setRowPairs([createEmptyPair(1)]);
    }
  }, []);

  function createEmptyPair(sequence) {
    const typePrefix = 'TRX';
    const relatedTo = `${typePrefix}${String(sequence).padStart(3, '0')}`;
    
    return {
      id: Date.now() + Math.random(),
      relatedTo: relatedTo,
      debit: {
        type: 'Transfer',
        category: 'Transfer',
        account: '',
        amount: '',
        name: '',
        trxDate: '',
        period: '',
        notes: '',
        paid: true,
        affectExpense: false,
        affectIncome: false
      },
      credit: {
        type: 'Transfer',
        category: 'Transfer',
        account: '',
        amount: '',
        name: '',
        trxDate: '',
        period: '',
        notes: '',
        paid: true,
        affectExpense: false,
        affectIncome: false
      }
    };
  }
  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        // Load accounts that allow transfers
        const accountsRef = collection(db, 'users', user.uid, 'account_setup');
        const accountsSnap = await getDocs(accountsRef);
        const accountsData = accountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAccounts(accountsData);

        // Load accounting periods that are open
        const periodsRef = collection(db, 'users', user.uid, 'Accounting_Periods');
        const periodsQuery = query(periodsRef, where('isOpen', '==', true));
        const periodsSnap = await getDocs(periodsQuery);
        const periodsData = periodsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAccountingPeriods(periodsData);

      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [user]);

  function createEmptyPair() {
    const typePrefix = mode === 'transfer-payment' ? 'TRX' : 'TRX';
    const relatedTo = `${typePrefix}${String(nextSequence).padStart(3, '0')}`;
    
    return {
      id: Date.now() + Math.random(),
      relatedTo: relatedTo,
      debit: {
        type: 'Transfer',
        category: 'Transfer',
        account: '',
        amount: '',
        name: '',
        trxDate: '',
        period: '',
        notes: '',
        paid: true,
        affectExpense: false,
        affectIncome: false
      },
      credit: {
        type: 'Transfer',
        category: 'Transfer',
        account: '',
        amount: '',
        name: '',
        trxDate: '',
        period: '',
        notes: '',
        paid: true,
        affectExpense: false,
        affectIncome: false
      }
    };
  }

  const addPair = () => {
    const newSequence = nextSequence + 1;
    setNextSequence(newSequence);
    setRowPairs([...rowPairs, createEmptyPair(newSequence)]);
  };

  const deletePair = (index) => {
    if (rowPairs.length > 1) {
      setRowPairs(rowPairs.filter((_, i) => i !== index));
    }
  };

  const duplicatePair = (index) => {
    const newSequence = nextSequence + 1;
    setNextSequence(newSequence);
    
    const pairToDuplicate = createEmptyPair(newSequence);
    // Copy values from the pair being duplicated (except relatedTo which is already new)
    pairToDuplicate.debit = { ...rowPairs[index].debit };
    pairToDuplicate.credit = { ...rowPairs[index].credit };
    
    const newPairs = [...rowPairs];
    newPairs.splice(index + 1, 0, pairToDuplicate);
    setRowPairs(newPairs);
  };

  const updatePair = (index, side, field, value) => {
    const newPairs = [...rowPairs];
    
    if (side === 'both') {
      // Update both sides for shared fields (date, period, notes, type)
      newPairs[index].debit[field] = value;
      newPairs[index].credit[field] = value;
      
      // Update category if type changes
      if (field === 'type') {
        newPairs[index].debit.category = value;
        newPairs[index].credit.category = value;
      }
    } else {
      // Update specific side
      newPairs[index][side][field] = value;
      
      // Auto-sync amount between DR and CR
      if (field === 'amount') {
        const otherSide = side === 'debit' ? 'credit' : 'debit';
        newPairs[index][otherSide].amount = value;
      }
      
      // Auto-generate name based on account
      if (field === 'account' && value) {
        const typeLabel = newPairs[index][side].type;
        if (side === 'debit') {
          newPairs[index][side].name = `${typeLabel} To ${value}`;
        } else {
          newPairs[index][side].name = `${typeLabel} From ${value}`;
        }
      }
    }
    
    setRowPairs(newPairs);
  };

  // Get accounts that allow transfers (active + allowTransfer)
  const getTransferAccounts = () => {
    return accounts.filter(acc => acc.active === true && acc.allowTransfer === true);
  };

  // Convert YYYY-MM-DD to DD-MM-YYYY
  const formatDateToDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  // Convert DD-MM-YYYY to YYYY-MM-DD for date input
  const formatDateToYYYYMMDD = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  // Check if a date is within open accounting periods
  const isDateInOpenPeriod = (dateStr) => {
    if (!dateStr) return false;
    
    const parts = dateStr.split('-');
    if (parts.length !== 3) return false;
    
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const year = parseInt(parts[2]);
    const checkDate = new Date(year, month, day);
    
    return accountingPeriods.some(period => {
      const periodParts = period.date.split('-');
      const periodYear = parseInt(periodParts[0]);
      const periodMonth = parseInt(periodParts[1]) - 1;
      
      const firstDay = new Date(periodYear, periodMonth, 1);
      const lastDay = new Date(periodYear, periodMonth + 1, 0);
      
      return checkDate >= firstDay && checkDate <= lastDay;
    });
  };

  // Handle transaction date change with validation
  const handleTrxDateChange = (index, value) => {
    const ddmmyyyy = formatDateToDDMMYYYY(value);
    
    if (isDateInOpenPeriod(ddmmyyyy)) {
      updatePair(index, 'both', 'trxDate', ddmmyyyy);
    } else {
      alert('Transaction date must be within an open accounting period month.');
      updatePair(index, 'both', 'trxDate', '');
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalDR = 0;
    let totalCR = 0;
    
    rowPairs.forEach(pair => {
      totalDR += parseFloat(pair.debit.amount) || 0;
      totalCR += parseFloat(pair.credit.amount) || 0;
    });
    
    return {
      dr: totalDR,
      cr: totalCR,
      variance: totalDR - totalCR
    };
  };

  // NO TRANSACTION SAVING - This will be implemented later
  const handleSave = () => {
    const totals = calculateTotals();
    if (totals.variance !== 0) {
      alert(`Cannot save: DR and CR must balance! Variance: £${Math.abs(totals.variance).toFixed(2)}`);
      return;
    }
    alert('Transaction saving not yet implemented. Please use "Save as Draft" to preserve your work.');
  };

  // DRAFT SAVING
  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      
      const draftData = {
        mode: mode,
        rowPairs: rowPairs,
        pairCount: rowPairs.length,
        nextSequence: nextSequence,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const draftsRef = collection(db, 'users', user.uid, 'Settings', 'WebPage', 'Drafts');
      await addDoc(draftsRef, draftData);

      alert(`Draft saved successfully with ${rowPairs.length} pair(s)! You can resume editing later.`);
      setSaving(false);
      onClose();
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Error saving draft: ' + error.message);
      setSaving(false);
    }
  };

  const totals = calculateTotals();

  // For Transfer/Payment mode only
  if (mode !== 'transfer-payment') {
    return <div>Expense/Income mode - use other component</div>;
  }

  return (
    <div className="bulk-transaction-modal">
      <div className="bulk-transaction-content">
        <div className="bulk-header">
          <h2>Bulk Transaction Entry - Transfer/Payment</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="bulk-toolbar">
          <button className="btn-action" onClick={addPair}>➕ Add Pair</button>
          <button className="btn-action" style={{background: 'linear-gradient(135deg, #ff7961, #ff6b9d)'}} onClick={handleSaveDraft} disabled={saving}>
            {saving ? '💾 Saving Draft...' : '📝 Save as Draft'}
          </button>
          <button className="btn-action" onClick={handleSave} disabled={saving}>
            💾 Save All (Not Available Yet)
          </button>
          <span className="row-count">
            {rowPairs.length} pair(s) | DR: £{totals.dr.toFixed(2)} | CR: £{totals.cr.toFixed(2)} | 
            <span style={{ color: totals.variance !== 0 ? '#ff5252' : '#26a69a', fontWeight: 'bold', marginLeft: '8px' }}>
              Var: £{totals.variance.toFixed(2)}
            </span>
          </span>
        </div>

        <div className="table-container">
          <table className="bulk-table">
            <thead>
              <tr>
                <th className="sticky-col">#</th>
                <th>Type *</th>
                <th>Account *</th>
                <th>Name</th>
                <th>DR</th>
                <th>CR</th>
                <th>Trx Date *</th>
                <th>Period *</th>
                <th>Notes</th>
                <th>Affect Exp</th>
                <th>Affect Inc</th>
                <th className="sticky-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rowPairs.map((pair, index) => (
                <React.Fragment key={pair.id}>
                  {/* Debit Row */}
                  <tr>
                    <td className="sticky-col" rowSpan="2">{index + 1}</td>
                    <td rowSpan="2">
                      <select
                        value={pair.debit.type}
                        onChange={(e) => updatePair(index, 'both', 'type', e.target.value)}
                        className="required"
                      >
                        <option value="Transfer">Transfer</option>
                        <option value="Payment">Payment</option>
                      </select>
                    </td>
                    <td>
                      <select
                        value={pair.debit.account}
                        onChange={(e) => updatePair(index, 'debit', 'account', e.target.value)}
                        className="required"
                      >
                        <option value="">Select...</option>
                        {getTransferAccounts().map(acc => (
                          <option key={acc.id} value={acc.name}>{acc.name}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ background: '#f8f9fa', fontStyle: 'italic', fontSize: '12px' }}>
                      {pair.debit.name || '-'}
                    </td>
                    <td style={{ width: '100px' }}>
                      <input
                        type="number"
                        step="0.01"
                        value={pair.debit.amount}
                        onChange={(e) => updatePair(index, 'debit', 'amount', e.target.value)}
                        placeholder="0.00"
                        className="required"
                        style={{ width: '90px' }}
                      />
                    </td>
                    <td style={{ background: '#f8f9fa', width: '100px' }}>-</td>
                    <td rowSpan="2">
                      <input
                        type="date"
                        value={formatDateToYYYYMMDD(pair.debit.trxDate)}
                        onChange={(e) => handleTrxDateChange(index, e.target.value)}
                        className="required"
                      />
                    </td>
                    <td rowSpan="2">
                      <select
                        value={pair.debit.period}
                        onChange={(e) => updatePair(index, 'both', 'period', e.target.value)}
                        className="required"
                      >
                        <option value="">Select...</option>
                        {accountingPeriods.map(period => (
                          <option key={period.id} value={formatDateToDDMMYYYY(period.date)}>
                            {formatDateToDDMMYYYY(period.date)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td rowSpan="2">
                      <input
                        type="text"
                        value={pair.debit.notes}
                        onChange={(e) => updatePair(index, 'both', 'notes', e.target.value)}
                        placeholder="Notes"
                      />
                    </td>
                    <td className="center">
                      <input
                        type="checkbox"
                        checked={pair.debit.affectExpense}
                        onChange={(e) => updatePair(index, 'debit', 'affectExpense', e.target.checked)}
                      />
                    </td>
                    <td className="center">
                      <input
                        type="checkbox"
                        checked={pair.debit.affectIncome}
                        onChange={(e) => updatePair(index, 'debit', 'affectIncome', e.target.checked)}
                      />
                    </td>
                    <td className="sticky-actions" rowSpan="2">
                      <button className="btn-icon" onClick={() => duplicatePair(index)} title="Duplicate Pair">📋</button>
                      <button className="btn-icon" onClick={() => deletePair(index)} title="Delete Pair" disabled={rowPairs.length === 1}>🗑️</button>
                    </td>
                  </tr>
                  
                  {/* Credit Row (indented) */}
                  <tr className="credit-row">
                    <td>
                      <select
                        value={pair.credit.account}
                        onChange={(e) => updatePair(index, 'credit', 'account', e.target.value)}
                        className="required"
                      >
                        <option value="">Select...</option>
                        {getTransferAccounts().map(acc => (
                          <option key={acc.id} value={acc.name}>{acc.name}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ background: '#f8f9fa', fontStyle: 'italic', fontSize: '12px' }}>
                      {pair.credit.name || '-'}
                    </td>
                    <td style={{ background: '#f8f9fa', width: '100px' }}>-</td>
                    <td style={{ width: '100px' }}>
                      <input
                        type="number"
                        step="0.01"
                        value={pair.credit.amount}
                        onChange={(e) => updatePair(index, 'credit', 'amount', e.target.value)}
                        placeholder="0.00"
                        className="required"
                        style={{ width: '90px' }}
                      />
                    </td>
                    <td className="center">
                      <input
                        type="checkbox"
                        checked={pair.credit.affectExpense}
                        onChange={(e) => updatePair(index, 'credit', 'affectExpense', e.target.checked)}
                      />
                    </td>
                    <td className="center">
                      <input
                        type="checkbox"
                        checked={pair.credit.affectIncome}
                        onChange={(e) => updatePair(index, 'credit', 'affectIncome', e.target.checked)}
                      />
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td className="sticky-col"></td>
                <td colSpan="3"><strong>TOTALS</strong></td>
                <td><strong>£{totals.dr.toFixed(2)}</strong></td>
                <td><strong>£{totals.cr.toFixed(2)}</strong></td>
                <td colSpan="3">
                  <strong style={{ color: totals.variance !== 0 ? '#ff5252' : '#26a69a' }}>
                    Variance: £{totals.variance.toFixed(2)}
                  </strong>
                </td>
                <td colSpan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="bulk-footer">
          <p className="help-text">* Required fields | DR and CR must balance | Dates in DD-MM-YYYY format | Save as Draft to preserve your work</p>
          <div className="footer-actions">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-draft" onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving Draft...' : '📝 Save as Draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkTransactionEntry;